/**
 * 25 Oct 2026 (D-1025-TODAY, §3.3) — "This week": the next seven days, one row each — the day and what is on it, in words
 * (counts by program; "Closed" and "Holiday" as words). A row shows that day by time above (the same `?date=` Today
 * always had); "Open the calendar" goes to the month. Read-only: the calendar's lines, which the API already
 * filtered to what the role may see.
 */
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { addDaysToKey } from '@shared/dates';
import { dayCounts, dayOverview, type CalLine } from '@shared/house-calendar';
import { formatDayKey, formatDayShort } from '@/lib/dates';
import { countWords, isWordCount } from '@/features/calendar/countWords';

/** "2 Housekeeping visits · 1 event"; "Closed"; "" when nothing is on the day. */
export function weekWords(lines: readonly CalLine[], dayKey: string, t: ReturnType<typeof useTranslation>['t']): string {
  const mine = dayOverview(
    lines.filter((l) => l.dayKey === dayKey),
    dayKey,
  );
  const counts = dayCounts(mine);
  return [...counts.filter((c) => !isWordCount(c)), ...counts.filter(isWordCount)].map((c) => countWords(c, t)).join(' · ');
}

export function ThisWeek({ todayKey, selected, lines, onPick }: { todayKey: string; selected: string; lines: readonly CalLine[]; onPick: (k: string) => void }) {
  const { t, i18n } = useTranslation();
  const days = Array.from({ length: 7 }, (_, i) => addDaysToKey(todayKey, i + 1));
  return (
    <section aria-labelledby="h-t1025-week" className="t184-section t1025-week" data-testid="t1025-week">
      <h2 id="h-t1025-week">{t('today1025.week')}</h2>
      <ul className="t1025-weeklist">
        {days.map((k) => {
          const what = weekWords(lines, k, t) || t('today1025.weekNothing');
          return (
            <li key={k}>
              <button type="button" className={`t1025-weekrow${k === selected ? ' is-selected' : ''}`} aria-pressed={k === selected} aria-label={`${t('today1025.weekRow', { day: formatDayKey(k, i18n.language), what })} — ${t('today1025.weekPick', { day: formatDayShort(k, i18n.language) })}`} onClick={() => onPick(k)} data-testid={`t1025-week-${k}`}>
                <span className="t1025-wday">{formatDayShort(k, i18n.language)}</span>
                <span className="t1025-wwhat">{what}</span>
              </button>
            </li>
          );
        })}
      </ul>
      <p className="t1025-opencal">
        <Link to="/calendar" className="btn secondary sm" data-testid="t1025-open-calendar">
          {t('today1025.openCalendar')} <span aria-hidden="true">›</span>
        </Link>
      </p>
    </section>
  );
}
